import { API_BASE_URL } from 'config/env-config';
import {
  deleteGoogleToken,
  deleteToken,
  getAccessTokenExp,
  setAccessToken,
  staffSessionHeaders,
} from 'utils/auth';

export interface GoogleTokenResponse {
  id_token: string;
  expires_in: number;
  token_type: string;
  // The server holds a Google refresh token, so the session can be refreshed.
  offline: boolean;
}

// Gets a new Google ID token for the staff session (X-Staff-Session). The
// server keeps Google's refresh token; the browser never sees it.
export async function refreshTokenApi(): Promise<GoogleTokenResponse> {
  const response = await fetch(`${API_BASE_URL}/api/admin/auth/refresh-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...staffSessionHeaders() },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw { ...error, status: response.status };
  }

  return response.json();
}

// Refresh this long before the ID token expires.
export const REFRESH_BEFORE_EXPIRY_SECONDS = 5 * 60;

export const tokenNeedsRefresh = () => {
  const exp = getAccessTokenExp();
  return !exp || exp - Date.now() / 1000 < REFRESH_BEFORE_EXPIRY_SECONDS;
};

let inFlight: Promise<boolean> | null = null;

// Refreshes the Google ID token once, however many callers ask at the same
// time. Resolves true on success. On failure it clears what is no longer
// usable: everything when the staff session has ended, only the Google token
// when the server can't refresh Google (signing in with Google again then
// skips the email code). Network errors clear nothing.
export function refreshGoogleToken(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = refreshTokenApi()
    .then(data => {
      setAccessToken(data.id_token, data.expires_in);
      return true;
    })
    .catch((error: any) => {
      console.warn('Token refresh failed', error?.error_code || error?.message);
      if (error?.status === 401) {
        if (error?.error_code === 'google_reauth_required') {
          deleteGoogleToken();
        } else {
          deleteToken();
        }
      } else if (error?.status === 403) {
        deleteToken();
      }
      return false;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
